import * as React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { DeleteAlfredClientDialog } from "@/components/alfred/delete-alfred-client-dialog"
import { Card, CardContent } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { SortableHeader } from "@/components/shared/sortable-header"
import { Button } from "@/components/ui/button"
import { ArrowRight, Users, Pencil, Trash2 } from "lucide-react"
import { useAlfredClients, useDeleteAlfredClient } from "@/hooks/use-alfred-clients"
import { ApiErrorState } from "@/components/shared/empty-states"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { useToast } from "@/hooks/use-toast"
import type { AlfredClientConfig } from "@/types/alfred-client"
import {
  ALFRED_CLIENT_LIST_ORIGIN_STATE,
  getAlfredClientListHistoryState,
  type AlfredClientListHistoryState,
  type AlfredClientSortColumn,
  type AlfredClientSortDirection,
} from "@/lib/alfred-client-navigation"

export function AlfredClientsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const restoredListState = getAlfredClientListHistoryState(location.state)
  const { data: clients, error, isLoading, mutate: refetchClients } = useAlfredClients()
  const deleteClient = useDeleteAlfredClient()

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [clientToDelete, setClientToDelete] = React.useState<AlfredClientConfig | null>(null)
  const [sortColumn, setSortColumn] = React.useState<AlfredClientSortColumn | null>(
    () => restoredListState?.sortColumn ?? null,
  )
  const [sortDirection, setSortDirection] = React.useState<AlfredClientSortDirection>(
    () => restoredListState?.sortDirection ?? "asc",
  )

  React.useEffect(() => {
    const alfredClientList: AlfredClientListHistoryState = {
      sortColumn,
      sortDirection,
    }

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: { alfredClientList } },
    )
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    sortColumn,
    sortDirection,
  ])

  const handleAddClient = () => {
    navigate("/alfred/clients/new", { state: ALFRED_CLIENT_LIST_ORIGIN_STATE })
  }

  const handleSort = (column: AlfredClientSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
  }

  const sortedClients = React.useMemo(() => {
    if (!clients) return []
    if (!sortColumn) return clients

    return [...clients].sort((a, b) => {
      let comparison = 0
      switch (sortColumn) {
        case "clientId":
          comparison = a.clientId.localeCompare(b.clientId)
          break
        case "venueCode":
          comparison = a.venueCode.localeCompare(b.venueCode)
          break
      }
      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [clients, sortColumn, sortDirection])

  const handleEditClient = (client: AlfredClientConfig) => {
    navigate(`/alfred/clients/${client.id}/edit`, { state: ALFRED_CLIENT_LIST_ORIGIN_STATE })
  }

  const handleDeleteClick = (client: AlfredClientConfig) => {
    setClientToDelete(client)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return
    try {
      await deleteClient.trigger(clientToDelete.id)
      setDeleteDialogOpen(false)
      toast({
        variant: "success",
        title: "Client deleted",
        description: `${clientToDelete.clientId} has been permanently removed.`,
      })
      refetchClients()
      setClientToDelete(null)
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to delete client",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  if (isLoading) {
    return (
      <>
        <DashboardHeader onAddClient={handleAddClient} />
        <main className="container mx-auto px-4 py-6">
          <DevicesTableSkeleton />
        </main>
      </>
    )
  }

  if (error) {
    return (
      <>
        <DashboardHeader onAddClient={handleAddClient} />
        <main className="container mx-auto px-4 py-6">
          <ApiErrorState onRetry={() => window.location.reload()} />
        </main>
      </>
    )
  }

  if (!clients || clients.length === 0) {
    return (
      <>
        <DashboardHeader onAddClient={handleAddClient} />
        <main className="container mx-auto px-4 py-6">
          <Card className="bg-card border-border" data-tour="clients-empty">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No clients found</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                There are no Alfred clients configured in the system.
              </p>
              <Button
                onClick={handleAddClient}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Add Client
              </Button>
            </CardContent>
          </Card>
        </main>
      </>
    )
  }

  return (
    <>
      <DashboardHeader onAddClient={handleAddClient} />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Alfred Clients</h1>
          <p className="text-sm text-muted-foreground">Manage and view all Alfred clients in the system</p>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <div className="table-scroll-x overflow-x-auto">
              <Table className="min-w-max w-full">
                <TableHeader>
                  <TableRow className="border-border hover:bg-card">
                    <SortableHeader
                      label="Client ID"
                      sortKey="clientId"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Venue Code"
                      sortKey="venueCode"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <TableHead className="text-muted-foreground font-medium w-32">Actions</TableHead>
                    <TableHead className="text-muted-foreground font-medium w-24 sticky right-0 bg-card z-10">
                      Edit / Delete
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedClients.map((client, index) => (
                    <TableRow
                      key={client.clientId}
                      className={`border-border transition-colors group ${
                        index % 2 === 1 ? "bg-muted/30 hover:bg-muted/50" : "hover:bg-muted/50"
                      }`}
                    >
                      <TableCell>
                        <div className="font-medium text-foreground">{client.clientId}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{client.venueCode}</div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          data-tour={index === 0 ? "client-devices" : undefined}
                          onClick={() => navigate(`/alfred/clients/${client.id}/devices`)}
                          className="bg-card border-input text-foreground hover:bg-secondary hover:text-foreground"
                        >
                          View Devices
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </TableCell>
                      <TableCell className="sticky right-0 bg-card z-10 min-w-24">
                        <div
                          className="flex items-center gap-1"
                          data-tour={index === 0 ? "client-actions" : undefined}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClient(client)}
                            className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 w-8"
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Edit client</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(client)}
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete client</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      <DeleteAlfredClientDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        clientId={clientToDelete?.clientId || ""}
        onConfirm={handleConfirmDelete}
        isLoading={deleteClient.isMutating}
      />
    </>
  )
}
